import React, { useEffect, useMemo, useState } from 'react';

import { applyShiftRange, matchesFilter, toggleKey } from './dualListSelection';
import './DualList.css';

const LEFT = 'left';
const RIGHT = 'right';

const DEFAULT_LABELS = {
  search: 'Buscar por número ou nome',
  addAll: 'Adicionar todos',
  removeAll: 'Remover todos',
  addChecked: (count) => `Adicionar marcados (${count})`,
  removeChecked: (count) => `Remover marcados (${count})`,
  add: (key) => `Adicionar ${key}`,
  remove: (key) => `Remover ${key}`,
  moveUp: (key) => `Mover ${key} para cima`,
  moveDown: (key) => `Mover ${key} para baixo`,
  empty: 'Nenhum item',
};

const keysSignature = (items, getKey) => items.map(getKey).join('\u0000');

function DualListPanel({
  side,
  title,
  items,
  getKey,
  getLabel,
  getSearchText,
  renderItemExtras,
  renderItemTags,
  canMove,
  onMove,
  reorder,
  headerExtra,
  labels,
}) {
  const [filter, setFilter] = useState('');
  const [checked, setChecked] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);

  const signature = keysSignature(items, getKey);
  useEffect(() => {
    setChecked(new Set());
    setAnchor(null);
  }, [signature]);

  const visible = useMemo(
    () => items.filter((item) => matchesFilter(getSearchText(item), filter)),
    [items, filter, getSearchText]
  );
  const visibleKeys = visible.map(getKey);
  const movableChecked = visible.filter((item) => checked.has(getKey(item)) && canMove(item, side));
  const movableVisible = visible.filter((item) => canMove(item, side));
  const isLeft = side === LEFT;
  const filtering = filter.trim() !== '';

  const changeFilter = (value) => {
    setFilter(value);
    setChecked(new Set());
    setAnchor(null);
  };

  const handleCheck = (event, key) => {
    const nextValue = event.target.checked;
    const shift = Boolean(event.nativeEvent && event.nativeEvent.shiftKey);
    setChecked((current) => (shift && anchor !== null
      ? applyShiftRange(visibleKeys, current, anchor, key, nextValue)
      : toggleKey(current, key, nextValue)));
    setAnchor(key);
  };

  const move = (selection) => {
    const keys = selection.map(getKey);
    if (keys.length === 0) return;
    onMove(keys);
  };

  return (
    <section className={`dual-list-panel dual-list-panel-${side}`} aria-label={title}>
      <header className="dual-list-header">
        <h2 className="dual-list-title">{`${title} (${items.length})`}</h2>
        {headerExtra}
        <input
          type="search"
          className="input is-small dual-list-search"
          placeholder={labels.search}
          aria-label={`${labels.search} - ${title}`}
          value={filter}
          onChange={(event) => changeFilter(event.target.value)}
        />
        <div className="dual-list-bulk">
          <button type="button" className="button is-small" disabled={movableChecked.length === 0} onClick={() => move(movableChecked)}>
            {isLeft ? labels.removeChecked(movableChecked.length) : labels.addChecked(movableChecked.length)}
          </button>
          <button type="button" className="button is-small" disabled={movableVisible.length === 0} onClick={() => move(movableVisible)}>
            {isLeft ? labels.removeAll : labels.addAll}
          </button>
        </div>
      </header>
      <ul className="dual-list-items">
        {visible.length === 0 && <li className="dual-list-empty">{labels.empty}</li>}
        {visible.map((item) => {
          const key = getKey(item);
          const fullIndex = items.indexOf(item);
          const movable = canMove(item, side);
          return (
            <li key={key} className="dual-list-item" data-key={key}>
              <input
                type="checkbox"
                className="dual-list-check"
                aria-label={`Marcar ${key}`}
                checked={checked.has(key)}
                onChange={(event) => handleCheck(event, key)}
              />
              <span className="dual-list-label">{getLabel(item)}</span>
              {renderItemTags && <span className="dual-list-tags">{renderItemTags(item, side)}</span>}
              {renderItemExtras && <span className="dual-list-extras">{renderItemExtras(item, side)}</span>}
              {isLeft && reorder && reorder.enabled && (
                <span className="dual-list-reorder">
                  <button type="button" className="button is-small" aria-label={labels.moveUp(key)} disabled={filtering || fullIndex === 0} onClick={() => reorder.onMoveUp(key)}>↑</button>
                  <button type="button" className="button is-small" aria-label={labels.moveDown(key)} disabled={filtering || fullIndex === items.length - 1} onClick={() => reorder.onMoveDown(key)}>↓</button>
                </span>
              )}
              <button
                type="button"
                className="button is-small dual-list-move"
                aria-label={isLeft ? labels.remove(key) : labels.add(key)}
                disabled={!movable}
                onClick={() => onMove([key])}
              >
                {isLeft ? '−' : '+'}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const alwaysMovable = () => true;

function DualList({
  leftTitle,
  rightTitle,
  leftItems,
  rightItems,
  getKey,
  getLabel,
  getSearchText,
  renderItemExtras,
  renderItemTags,
  canMove = alwaysMovable,
  onMoveToLeft,
  onMoveToRight,
  reorder,
  leftHeaderExtra,
  rightHeaderExtra,
  labels,
}) {
  const mergedLabels = { ...DEFAULT_LABELS, ...(labels || {}) };
  const searchText = getSearchText || getLabel;
  const shared = { getKey, getLabel, getSearchText: searchText, renderItemExtras, renderItemTags, canMove, labels: mergedLabels };
  return (
    <div className="dual-list">
      <DualListPanel {...shared} side={LEFT} title={leftTitle} items={leftItems} onMove={onMoveToRight} reorder={reorder} headerExtra={leftHeaderExtra} />
      <DualListPanel {...shared} side={RIGHT} title={rightTitle} items={rightItems} onMove={onMoveToLeft} headerExtra={rightHeaderExtra} />
    </div>
  );
}

export default DualList;
